import {
  decodePathCommands,
  commandsToPath,
  computeEndTime,
  tessellateCommands,
} from './pathLinker';
import { POST_GAP_MS } from '../logic/directedChart';

type Pt = { x: number; y: number };
type PathCmd = ReturnType<typeof decodePathCommands>[number];
type AnyNote = {
  tStart: number;
  /** Optional alias used by some generation code */
  tMs?: number;
  tEnd?: number;
  duration?: number;
  points?: Pt[];
  pts?: Pt[];
  path?: any[];
  repeats?: number;
  beatDivisor?: number;
  pixelLength?: number;
  sv?: number;
  bpmChanges?: any[];
  pos?: Pt;
};

const d2 = (a: Pt, b: Pt) => { const dx = a.x - b.x, dy = a.y - b.y; return dx*dx + dy*dy; };

const hasPts = (n: AnyNote) => Array.isArray(n.points) || Array.isArray(n.pts);
const hasPath = (n: AnyNote) => Array.isArray(n.path) && n.path.length > 0;
export const isSlider = (n: AnyNote) => hasPts(n) || hasPath(n);

// Convert raw points into path commands. Even simple two-point segments are
// encoded as degenerate quadratic Béziers to guarantee downstream consumers
// always receive ``B`` commands. Longer point arrays are approximated by a
// sequence of quadratic curves following a Catmull–Rom spline.
export function pointsToCommands(pts: Pt[]): PathCmd[] {
  /** @type {PathCmd[]} */
  const cmds: PathCmd[] = [];
  if (pts.length === 0) return cmds;
  cmds.push({ cmd: 'M', x: pts[0].x, y: pts[0].y });
  if (pts.length === 1) return cmds;
  if (pts.length === 2) {
    const [p0, p1] = pts;
    // Encode simple two-point segments as degenerate quadratic Béziers so that
    // all emitted paths rely exclusively on ``B`` commands. The control point is
    // placed at the start which produces a straight line from p0→p1.
    cmds.push({ cmd: 'B', cx: p0.x, cy: p0.y, x: p1.x, y: p1.y });
    return cmds;
  }
  if (pts.length === 3) {
    cmds.push({ cmd: 'B', cx: pts[1].x, cy: pts[1].y, x: pts[2].x, y: pts[2].y });
    return cmds;
  }
  const d = (a: Pt, b: Pt) => Math.hypot(a.x - b.x, a.y - b.y);
  const lerp = (A: Pt, B: Pt, ta: number, tb: number, t: number): Pt => {
    const w = (t - ta) / (tb - ta);
    return { x: A.x + w * (B.x - A.x), y: A.y + w * (B.y - A.y) };
  };
  const alpha = 0.5;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = i === 0 ? pts[0] : pts[i - 1];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = i + 2 < pts.length ? pts[i + 2] : pts[pts.length - 1];
    const t0 = 0;
    const t1 = t0 + Math.pow(d(p0, p1), alpha);
    const t2 = t1 + Math.pow(d(p1, p2), alpha);
    let t3 = t2 + Math.pow(d(p2, p3), alpha);
    if (t3 === t2) t3 = t2 + 1; // avoid zero-length segment when p2 === p3
    const tt = t1 + 0.5 * (t2 - t1);
    const A1 = lerp(p0, p1, t0, t1, tt);
    const A2 = lerp(p1, p2, t1, t2, tt);
    const A3 = lerp(p2, p3, t2, t3, tt);
    const B1 = lerp(A1, A2, t0, t2, tt);
    const B2 = lerp(A2, A3, t1, t3, tt);
    const Q = lerp(B1, B2, t1, t2, tt);
    const cx = 2 * Q.x - 0.5 * (p1.x + p2.x);
    const cy = 2 * Q.y - 0.5 * (p1.y + p2.y);
    cmds.push({ cmd: 'B', cx, cy, x: p2.x, y: p2.y });
  }
  return cmds;
}

// Remove nearly colinear intermediate points so that straight segments do not
// produce a dense series of degenerate Bézier curves. This helps turn chains of
// straight segments into smoother arcs by retaining only the significant joints
// in the path.
function simplifyColinear(pts: Pt[], angleThreshold = Math.PI / 180): Pt[] {
  if (!Array.isArray(pts) || pts.length <= 2) return pts.slice();
  const res: Pt[] = [pts[0]];
  let prevIdx = 0;
  for (let i = 1; i < pts.length - 1; i++) {
    const prev = pts[prevIdx];
    const cur = pts[i];
    const next = pts[i + 1];
    const v1x = cur.x - prev.x;
    const v1y = cur.y - prev.y;
    const v2x = next.x - cur.x;
    const v2y = next.y - cur.y;
    const len1 = Math.hypot(v1x, v1y);
    const len2 = Math.hypot(v2x, v2y);
    if (len1 === 0 || len2 === 0) continue; // skip duplicate points
    const cos = (v1x * v2x + v1y * v2y) / (len1 * len2);
    const angle = Math.acos(Math.min(1, Math.max(-1, cos)));
    if (angle > angleThreshold) {
      res.push(cur);
      prevIdx = i;
    }
  }
  res.push(pts[pts.length - 1]);
  return res;
}

// Normalize to a command array for merge checks. When only point arrays are
// present they are converted to path commands and the original note is updated
// to store a path instead.
const extractPath = (n: AnyNote): PathCmd[] => {
  if (Array.isArray((n as any)._pathCmds)) return (n as any)._pathCmds;
  let cmds: PathCmd[] = [];
  if (Array.isArray(n.path)) {
    cmds = decodePathCommands(n.path);
  } else if (Array.isArray(n.points) || Array.isArray(n.pts)) {
    const pts = (n.points ?? n.pts) as Pt[];
    cmds = pointsToCommands(pts);
    if (cmds.length > 0) {
      (n as any).path = commandsToPath(cmds);
      delete (n as any).points;
      delete (n as any).pts;
    }
  }
  (n as any)._pathCmds = cmds;
  return cmds;
};

const pathStart = (cmds: PathCmd[]): Pt | null => {
  for (const c of cmds) {
    if (c.cmd === 'M' || c.cmd === 'L') return { x: c.x, y: c.y };
    if (c.cmd === 'B') return { x: c.x, y: c.y }; // shouldn't happen but safe
  }
  return null;
};

const pathEnd = (cmds: PathCmd[]): Pt | null => {
  let cur: Pt | null = null;
  for (const c of cmds) {
    if (c.cmd === 'M' || c.cmd === 'L') cur = { x: c.x, y: c.y };
    else if (c.cmd === 'B') cur = { x: c.x, y: c.y };
  }
  return cur;
};

// Exported so callers can reliably compute note start times for diagnostics and
// scheduling logic outside this module.
export const startOf = (n: AnyNote): number =>
  (n.tStart ?? (n as any).tMs) as number;

// Expose the computed end time helper so callers performing pre-merge
// diagnostics can accurately determine when a slider finishes even if the
// source note omitted ``tEnd``. This mirrors the internal logic used by the
// merge routine and guarantees consistent timing calculations across modules.
export const endOf = (n: AnyNote): number => {
  if (n.tEnd != null) return n.tEnd;
  const start = startOf(n);
  if (n.duration != null && Number.isFinite(start)) return start + n.duration;

  if (n.pixelLength == null && (hasPts(n) || hasPath(n))) {
    const cmds = extractPath(n);
    if (cmds.length > 0) {
      const pts = tessellateCommands(cmds);
      let len = 0;
      for (let i = 1; i < pts.length; i++) {
        len += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
      }
      (n as any).pixelLength = len;
    }
  }

  const sv = n.sv ?? 1;
  if (n.pixelLength != null && Number.isFinite(start)) {
    if (n.sv == null) {
      console.warn(
        `[coalesceSliderChains] missing sv for slider at ${start}ms; defaulting to 1`,
      );
    }
    const repeats = Math.max(1, Number(n.repeats) || 0);
    const tEnd = computeEndTime(
      start,
      n.pixelLength * repeats,
      sv,
      n.bpmChanges ?? [],
    );
    (n as any).tEnd = tEnd;
    (n as any).duration = tEnd - start;
    return tEnd;
  }
  // if pixelLength or start is invalid, we can’t reliably time-chain → treat as non-consecutive
  return Number.NaN;
};

export function coalesceSliderChains(
  notes: AnyNote[],
  tolMs = POST_GAP_MS + 800,
  tolPx = 10,
): AnyNote[] {
  const tolPx2 = tolPx * tolPx;
  const arr = notes
    .slice()
    .sort((a, b) => startOf(a) - startOf(b));
  const out: AnyNote[] = [];

  for (let i = 0; i < arr.length; ) {
    const first = arr[i];
    if (!isSlider(first)) { out.push(first); i++; continue; }

    let j = i;
    let last = arr[i];
    const chain: AnyNote[] = [last];

    for (;;) {
      const next = arr[j + 1];
      if (!next || !isSlider(next)) break;

      const tEnd = endOf(last);
      if (!Number.isFinite(tEnd)) break;     // can’t chain without end time

      const timeTouch = Math.abs(startOf(next) - tEnd) <= tolMs;

      const Apath = extractPath(last);
      const Bpath = extractPath(next);
      if (Apath.length === 0 || Bpath.length === 0) break;

      const Aend = pathEnd(Apath);
      const Bstart = pathStart(Bpath);
      if (!Aend || !Bstart) break;
      const spatialTouch = d2(Aend, Bstart) <= tolPx2;

      if (!(timeTouch && spatialTouch)) {
        // Surface information about why a potential chain failed to merge so
        // that upstream callers can debug unexpected consecutive sliders.
        const idxA = notes.indexOf(last);
        const idxB = notes.indexOf(next);
        if (timeTouch && !spatialTouch) {
          console.warn(
            `[coalesceSliderChains] chain break between #${idxA} and #${idxB}: ` +
              `spatial gap ${Math.sqrt(d2(Aend, Bstart)).toFixed(2)} > ${tolPx}`,
          );
        } else if (!timeTouch && spatialTouch) {
          console.warn(
            `[coalesceSliderChains] chain break between #${idxA} and #${idxB}: ` +
              `time gap ${Math.abs(startOf(next) - tEnd)}ms > ${tolMs}ms`,
          );
        }
        break;
      }

      chain.push(next);
      last = next;
      j++;
    }

    if (chain.length === 1) { out.push(first); i++; continue; }

    // merge → concat path commands, drop duplicate joints
    /** @type {PathCmd[]} */
    const mergedCmds: PathCmd[] = [];
    for (let c = 0; c < chain.length; c++) {
      const cmds = extractPath(chain[c]);
      if (c === 0) {
        mergedCmds.push(...cmds);
      } else {
        const prevEnd = pathEnd(mergedCmds) as Pt;
        let startIdx = 0;
        if (cmds[0]?.cmd === 'M') {
          const start = { x: cmds[0].x, y: cmds[0].y };
          if (d2(prevEnd, start) <= tolPx2) startIdx = 1; // drop duplicate move
          else cmds[0] = { cmd: 'L', x: cmds[0].x, y: cmds[0].y };
        }
        for (let u = startIdx; u < cmds.length; u++) mergedCmds.push(cmds[u]);
      }
    }

    const firstEnd = endOf(chain.at(-1)!);
    const merged: AnyNote = { ...chain[0] };
    merged.tStart = chain[0].tStart;
    merged.tEnd = firstEnd;
    merged.duration = firstEnd - merged.tStart;
    merged.repeats = 1;
    // Resample the full merged path so that it is represented by a continuous
    // Catmull–Rom/quad Bézier curve. This converts any leftover straight line
    // ``L`` segments into quadratic ``B`` commands across the entire chain.
    // ``simplifyColinear`` collapses long straight runs before Catmull-Rom
    // fitting so the result forms broad arcs instead of a series of tiny
    // straight segments.
    const mergedPts = tessellateCommands(mergedCmds);
    const simplifiedPts = simplifyColinear(mergedPts);
    const smoothCmds = pointsToCommands(simplifiedPts);

    merged.path = commandsToPath(smoothCmds);
    merged.points = tessellateCommands(smoothCmds);
    delete (merged as any).pts;
    (merged as any)._pathCmds = smoothCmds;

    // Compute and store the total pixel length of the merged slider.
    let len = 0;
    for (let p = 1; p < merged.points.length; p++) {
      const a = merged.points[p - 1];
      const b = merged.points[p];
      len += Math.hypot(b.x - a.x, b.y - a.y);
    }
    // ``merged.points`` are expressed in stage units where one unit corresponds
    // to 100 px. The original ``buildHitObjects`` logic stores ``pixelLength``
    // in pixel coordinates, so we need to apply the same scaling here after
    // recomputing the merged path length.
    merged.pixelLength = len * 100;

    out.push(merged);
    i = j + 1;
  }

  return out;
}

/**
 * Throw an error if the provided note array contains any consecutive sliders
 * that are effectively touching. Non-slider notes are ignored so that a circle
 * placed at the same timestamp cannot mask a pair of back-to-back sliders.
 *
 * The thrown error includes the time gap between the slider pair to make
 * debugging easier when the guard triggers.
 */
export function assertNoConsecutiveSliders(
  notes: AnyNote[],
  tolMs = POST_GAP_MS,
): void {
  const sliders = notes.filter(isSlider);
  for (let i = 1; i < sliders.length; i++) {
    const prev = sliders[i - 1];
    const curr = sliders[i];
    const prevIdx = notes.indexOf(prev);
    const currIdx = notes.indexOf(curr);
    const prevStart = startOf(prev);
    const currStart = startOf(curr);
    const prevEnd = endOf(prev);
    const currEnd = endOf(curr);

    if (!Number.isFinite(prevEnd) || !Number.isFinite(currStart)) {
      console.warn(
        `Consecutive sliders with invalid timing: ` +
          `#${prevIdx} end=${prevEnd}ms, #${currIdx} start=${currStart}ms`,
      );
      const gap = currStart - prevEnd;
      throw new Error(
        `Consecutive sliders detected in generated data: ` +
          `#${prevIdx} [${prevStart}ms → ${prevEnd}ms] followed by ` +
          `#${currIdx} [${currStart}ms → ${currEnd}ms] ` +
          `(gap ${gap}ms)`,
      );
    }

    const gap = currStart - prevEnd;

    // Only treat as an error if the sliders are effectively contiguous. Widely
    // spaced sliders are allowed and are interpreted as separate objects.
    if (gap <= tolMs) {
      const prevPath = extractPath(prev);
      const currPath = extractPath(curr);
      const prevPos = pathStart(prevPath);
      const prevEndPos = pathEnd(prevPath);
      const currPos = pathStart(currPath);
      const currEndPos = pathEnd(currPath);
      throw new Error(
        `Consecutive sliders detected in generated data: ` +
          `#${prevIdx} [${prevStart}ms → ${prevEnd}ms] ` +
          `(${prevPos?.x},${prevPos?.y})→(${prevEndPos?.x},${prevEndPos?.y}) followed by ` +
          `#${currIdx} [${currStart}ms → ${currEnd}ms] ` +
          `(${currPos?.x},${currPos?.y})→(${currEndPos?.x},${currEndPos?.y}) ` +
          `(gap ${gap}ms)`,
      );
    }
  }
}

/**
 * Throw an error if any slider's end point is too close to its start point.
 * This guard helps catch cases where slider merging failed and produced a
 * nearly zero-length slider.
 */
export function assertNoDegenerateSliders(notes: AnyNote[], tolPx = 0.5): void {
  const tol2 = tolPx * tolPx;
  for (let i = 0; i < notes.length; i++) {
    const n = notes[i];
    if (!isSlider(n)) continue;

    // Estimate the total pixel length before ``extractPath`` mutates the note.
    let len = typeof n.pixelLength === 'number' ? n.pixelLength : 0;
    if (len === 0) {
      const pts = (n.points ?? (n as any).pts) as Pt[] | undefined;
      if (Array.isArray(pts)) {
        for (let p = 1; p < pts.length; p++) {
          const a = pts[p - 1];
          const b = pts[p];
          len += Math.hypot(b.x - a.x, b.y - a.y);
        }
      }
    }

    const cmds = extractPath(n);
    const start = pathStart(cmds);
    const end = pathEnd(cmds);
    if (!start || !end) continue;

    // Only treat as degenerate if the total travelled distance is also tiny.
    if (d2(start, end) <= tol2 && len <= tolPx) {
      const tStart = startOf(n);
      const tEnd = endOf(n);
      throw new Error(
        `Slider #${i} start and end positions too close: (${start.x},${start.y}) → (${end.x},${end.y}) [${tStart}ms → ${tEnd}ms] (len ${len})`,
      );
    }
  }
}
